import { defineStore } from 'pinia'
import type { CategoryKey } from './currentCategory'
import { authenticatedFetch } from './state'

interface ApiStatResponse {
  score: number
  progress: number
  percentile: number
  distribution: number[]
  progress_history: ProgressPoint[]
}

export type ProgressPoint = { date: string; progress: number }

export interface StatResponse extends Omit<ApiStatResponse, 'progress_history'> {
  progressHistory: ProgressPoint[]
}

export const useCompanyStatsStore = defineStore('companyStats', {
  state: () => ({
    stats: {} as Record<string, StatResponse>,
    lastFetch: 0,
    cacheDuration: 30_000,
    fetchPromise: null as Promise<void> | null
  }),

  actions: {
    async fetchStats(force = false) {
      if (this.fetchPromise) {
        return this.fetchPromise
      }

      if (!force && Date.now() - this.lastFetch < this.cacheDuration) {
        return
      }

      this.fetchPromise = this._doFetchStats()
      
      try {
        await this.fetchPromise
      } finally {
        this.fetchPromise = null
      }
    },

    async _doFetchStats() { 
      try {
        const res = await authenticatedFetch(`${import.meta.env.VITE_API_BASE}/users/me/stats`, {
          credentials: 'include'
        })
        
        const data = await res.json() as Record<string, ApiStatResponse | null>
        
        const mapped: Record<string, StatResponse> = {}
        for (const [key, val] of Object.entries(data)) {
          if (val) {
            mapped[key] = {
              score: val.score,
              progress: val.progress,
              percentile: val.percentile,
              distribution: val.distribution,
              progressHistory: val.progress_history
            }
          }
        }
        
        this.stats = mapped
        this.lastFetch = Date.now()
        
      } catch (error) {
        console.error('Failed to load stats:', error)
        throw error
      }
    },

    async updateStats(category: CategoryKey, score: number, progress: number) {
      try {
        const res = await authenticatedFetch(
          `${import.meta.env.VITE_API_BASE}/users/me/stats/${encodeURIComponent(category)}`,
          {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ score, progress })
          }
        )

        const data = await res.json()
        
        this.stats[category] = {
          score: data.score,
          progress: data.progress,
          percentile: data.percentile,
          distribution: data.distribution,
          progressHistory: data.progress_history
        }

      } catch (error) {
        console.error('Failed to update stats:', error)
        throw error
      }
    }
  }
})

